import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";

import type { DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import type {
  CashFlowRow,
  PeriodMeta,
} from "@/hooks/finance/cash-flow/useCashFlow";

import { StatementTableRow } from "./StatementTableRow";
import { makeFormatValue } from "./statement-table.utils";
import { exportCashFlowStatementToExcel } from "./exportExcel";

type Props = {
  rows: CashFlowRow[];
  periodLabels: { key: string; label: string }[];
  periodKeys: string[];
  isLoading: boolean;
  displayCurrency: DisplayCurrency;
  dateFrom?: string;
  dateTo?: string;

  openingMetaByPeriod?: Map<string, PeriodMeta>;
  closingMetaByPeriod?: Map<string, PeriodMeta>;
};

export function StatementTable({
  rows,
  periodLabels,
  periodKeys,
  isLoading,
  displayCurrency,
  dateFrom,
  dateTo,
  openingMetaByPeriod,
  closingMetaByPeriod,
}: Props) {
  const formatValue = makeFormatValue(displayCurrency);

  return (
    <Card className="border-none shadow-lg overflow-hidden bg-white">
      <CardHeader className="bg-slate-900 border-none pb-4">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white text-base">
              Consolidated Statement
            </CardTitle>
            <CardDescription className="text-slate-400 text-[10px]">
              Indented ledger by GAAP classification
            </CardDescription>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() =>
                void exportCashFlowStatementToExcel(
                  rows,
                  periodLabels,
                  displayCurrency,
                  `cash-flow-statement-${format(new Date(), "yyyyMMdd")}.xlsx`
                )
              }
              title="Download as Excel"
            >
              <Download className="h-3 w-3 mr-1" />
              Excel
            </Button>
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px] uppercase font-black tracking-widest">
              Balanced
            </Badge>
            <div className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-[9px] font-mono select-all">
              REF: CF-{format(new Date(), "yyMMdd")}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-32 bg-slate-50/20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-20 bg-slate-50 w-64 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">
                    Line Item
                  </th>

                  {periodLabels.map((p) => (
                    <th
                      key={p.key}
                      className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[120px]"
                    >
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <StatementTableRow
                    key={row.key}
                    row={row}
                    periodLabels={periodLabels}
                    periodKeys={periodKeys}
                    formatValue={formatValue}
                    openingMetaByPeriod={openingMetaByPeriod}
                    closingMetaByPeriod={closingMetaByPeriod}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}